import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { generateModulesUrl } from '../../../utils/urlSecurity';
import { useCart } from '../../../contexts/CartContext';

const CoursesPage = () => {
  const [courses, setCourses] = useState([]);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [loading, setLoading] = useState({
    courses: true,
    enrolled: true,
    enrolling: null
  });
  const [error, setError] = useState({
    courses: null,
    enrolled: null
  });

  const { user } = useAuth();
  const navigate = useNavigate();
  const { cartItems, addCourseToCart } = useCart();
  const [addingToCart, setAddingToCart] = useState(null);
  
  // Ref to track ongoing enrollment requests to prevent duplicates
  const enrollmentRequestsRef = useRef(new Set());

  // Default fallback image for courses
  const defaultCourseImage = 'https://via.placeholder.com/300x200/17a2b8/ffffff?text=Course+Image';

  const cartCourseIds = useMemo(
    () => cartItems.map((item) => item.courseId),
    [cartItems]
  );

  const extractSkillName = (skill) => {
    if (!skill) {
      return null;
    }

    if (typeof skill === 'string') {
      return skill;
    }

    if (typeof skill === 'object') {
      const keyCandidates = ['name', 'skill_name', 'skillName', 'title', 'label'];
      const directMatch = keyCandidates
        .map((key) => (typeof skill[key] === 'string' ? skill[key] : null))
        .find((value) => value && value.trim().length > 0);

      if (directMatch) {
        return directMatch;
      }

      if (skill.Skill) {
        return extractSkillName(skill.Skill);
      }

      if (skill.skill) {
        return extractSkillName(skill.skill);
      }

      const firstStringValue = Object.values(skill).find(
        (value) => typeof value === 'string' && value.trim().length > 0
      );

      if (firstStringValue) {
        return firstStringValue;
      }
    }

    return null;
  };

  const deriveCourseSkills = (course) => {
    if (!course) {
      return [];
    }

    const skillSources = [
      course.skills,
      course.CourseSkills,
      course.skills_accrued,
      course.skillsAccrued,
      course.skill_tags
    ];

    return skillSources
      .flatMap((collection) => (Array.isArray(collection) ? collection : []))
      .map(extractSkillName)
      .filter((skill, index, array) => Boolean(skill) && array.indexOf(skill) === index);
  };

  const resolveCoursePrice = (course) => {
    if (!course) {
      return null;
    }

    const priceCandidates = [
      course.price,
      course.course_price,
      course.coursePrice,
      course.cost,
      course.amount,
      course.CoursePrice?.price,
      course.CoursePricing?.price,
      course.pricing?.price,
      course.pricing?.amount,
      course.Pricing?.price,
      course.Pricing?.amount
    ];

    return priceCandidates.find((value) => value !== undefined && value !== null);
  };

  const getCoursePriceLabel = (course) => {
    const priceValue = resolveCoursePrice(course);

    if (priceValue === undefined || priceValue === null) {
      return 'Free';
    }

    if (typeof priceValue === 'string' && priceValue.trim().length === 0) {
      return 'Free';
    }

    const numericPrice = Number(priceValue);

    if (Number.isNaN(numericPrice)) {
      return typeof priceValue === 'string' ? priceValue : 'Paid';
    }

    if (numericPrice === 0) {
      return 'Free';
    }

    return `₹${numericPrice.toFixed(2)}`;
  };

  // Fetch all active courses
  const fetchCourses = async () => {
    try {
      setLoading(prev => ({ ...prev, courses: true }));
      setError(prev => ({ ...prev, courses: null }));
      
      console.log('🔍 Fetching courses...');
      console.log('🔑 Access token:', localStorage.getItem('accessToken') ? 'Present' : 'Missing');
      
      const response = await api.courses.getAll();
      
      console.log('✅ Courses response:', response.data);
      
      if (response.data.success) {
        setCourses(response.data.data || []);
      }
    } catch (error) {
      console.error('❌ Error fetching courses:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      
      setError(prev => ({ 
        ...prev, 
        courses: error.response?.data?.message || 'Failed to load courses' 
      }));
    } finally {
      setLoading(prev => ({ ...prev, courses: false }));
    }
  };

  // Fetch enrolled courses for the user
  const fetchEnrolledCourses = async () => {
    try {
      setLoading(prev => ({ ...prev, enrolled: true }));
      setError(prev => ({ ...prev, enrolled: null }));
      
      const response = await api.enrollments.getByUser(user.id);
      
      if (response.data.success) {
        setEnrolledCourses(response.data.data || []);
      } else {
        // Fallback for old format
        setEnrolledCourses(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching enrolled courses:', error);
      
      // Handle specific validation errors
      if (error.response?.status === 403) {
        setError(prev => ({ 
          ...prev, 
          enrolled: 'Your account is inactive or has been deleted. Please contact support.' 
        }));
      } else {
        setError(prev => ({ 
          ...prev, 
          enrolled: error.response?.data?.message || 'Failed to load enrolled courses' 
        }));
      }
    } finally {
      setLoading(prev => ({ ...prev, enrolled: false }));
    }
  };

  // Handle course enrollment with duplicate prevention and immediate state update
  const handleEnroll = async (courseId) => {
    // Prevent duplicate enrollment requests
    if (enrollmentRequestsRef.current.has(courseId)) {
      toast.warning('Enrollment in progress. Please wait...');
      return;
    }

    // Check if already enrolled (additional safety check)
    if (isEnrolled(courseId)) {
      toast.info('You are already enrolled in this course.');
      return;
    }

    try {
      // Mark this course as being processed
      enrollmentRequestsRef.current.add(courseId);
      setLoading(prev => ({ ...prev, enrolling: courseId }));
      
      const response = await api.enrollments.create(user.id, courseId);
      
      if (response.data.success) {
        toast.success('Successfully enrolled in course!');
        
        // Get the course details for immediate state update
        const course = courses.find(c => c.id === courseId);
        
        if (course && response.data.data) {
          // Create the enrollment object to add to enrolled courses
          const newEnrollment = {
            id: response.data.data.id,
            course_id: courseId,
            user_id: user.id,
            enrollment_date: response.data.data.enrollment_date,
            status: response.data.data.status,
            deleted: false,
            Course: {
              id: course.id,
              name: course.title,
              title: course.title,
              description: course.description,
              duration: course.duration,
              category_id: course.category_id,
              difficulty_level: course.difficulty_level,
              course_image: course.course_image,
              category: course.category,
              difficulty_level: course.difficulty_level
            }
          };
          
          // Immediately update enrolled courses state
          setEnrolledCourses(prev => [newEnrollment, ...prev]);
        }
        
      }
    } catch (error) {
      console.error('Error enrolling in course:', error);
      
      // Handle specific validation errors
      if (error.response?.status === 400) {
        const message = error.response?.data?.message || 'Failed to enroll in course';
        if (message.includes('already enrolled')) {
          toast.info('You are already enrolled in this course.');
          // Refresh enrolled courses to sync state
          fetchEnrolledCourses();
        } else {
          toast.error(message);
        }
      } else if (error.response?.status === 403) {
        toast.error('Your account is inactive or has been deleted. Please contact support.');
      } else if (error.response?.status === 404) {
        toast.error('Course not found or not available for enrollment.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to enroll in course');
      }
    } finally {
      // Remove from processing set and reset loading state
      enrollmentRequestsRef.current.delete(courseId);
      setLoading(prev => ({ ...prev, enrolling: null }));
    }
  };

  // Handle view course modules
  const handleViewCourse = (courseId) => {
    const modulesUrl = generateModulesUrl(courseId, 'user');
    navigate(modulesUrl);
  };

  // Check if user is already enrolled in a course
  const isEnrolled = (courseId) => {
    return enrolledCourses.some(enrollment => {
      const enrollmentCourseId = enrollment.course_id || enrollment.Course?.id;
      return enrollmentCourseId === courseId;
    });
  };

  useEffect(() => {
    fetchCourses();
    if (user?.id) {
      fetchEnrolledCourses();
    }
  }, [user?.id]);

  const availableCourses = courses.filter(course => !isEnrolled(course.id));

  const isInCart = (courseId) => cartCourseIds.includes(courseId);

  const handleAddToCart = async (courseId) => {
    try {
      setAddingToCart(courseId);
      await addCourseToCart(courseId);
    } finally {
      setAddingToCart(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 sm:p-6 md:p-8 lg:p-10">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl px-5 py-4 sm:p-6 border border-purple-100 shadow-xl">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white text-xl sm:text-2xl shadow-md">
                <i className="fas fa-book-open"></i>
              </div>
              <div>
                <h2 className="text-left text-2xl sm:text-2xl font-bold text-gray-900 mb-1">Browse Courses</h2>
                <p className="text-gray-600 text-sm sm:text-base m-0">
                  Discover and enroll in courses to enhance your skills
                </p>
              </div>
            </div>
            {availableCourses.length > 0 && (
              <Badge bsPrefix="custom-hamburger" className="text-white bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 font-semibold rounded-lg sm:rounded-xl shadow-md transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 px-3">
                {availableCourses.length} Available
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-purple-100 shadow-xl p-4 sm:p-6">
        {loading.courses ? (
          <div className="text-center py-12 sm:py-16">
            <div className="inline-block animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-purple-600 border-t-transparent mb-4"></div>
            <p className="text-gray-600 text-base sm:text-lg font-medium">Loading courses...</p>
          </div>
        ) : error.courses ? (
          <Alert variant="danger" className="rounded-xl border-2 border-red-200 bg-red-50">
            <div className="flex items-center gap-3">
              <i className="fas fa-exclamation-triangle text-red-600 text-xl"></i>
              <span className="font-semibold">{error.courses}</span>
            </div>
          </Alert>
        ) : availableCourses.length === 0 ? (
          <div className="text-center py-12 sm:py-16">
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <i className="fas fa-check-circle text-white text-3xl sm:text-3xl"></i>
            </div>
            <h5 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">All caught up!</h5>
            <p className="text-gray-600 text-base sm:text-lg">You are enrolled in every available course.</p>
          </div>
        ) : (
          <Row className="g-4">
            {availableCourses.map((course) => {
              const courseSkills = deriveCourseSkills(course);
              const priceLabel = getCoursePriceLabel(course);
              const courseTitle = course.title || course.name || 'Course';
              const coursePrice = Number(course.price || 0);
              const isFreeCourse = coursePrice === 0;
              const inCart = isInCart(course.id);
                
                return (
                    <Col lg={4} md={6} sm={12} key={course.id}>
                      <Card className="h-100 border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                        <div style={{ height: '200px', overflow: 'hidden', backgroundColor: '#f8f9fa', position: 'relative' }}>
                          <Card.Img
                            variant="top"
                            src={course.course_image || defaultCourseImage}
                            alt={courseTitle}
                            style={{ 
                              width: '100%',
                              height: '100%', 
                              objectFit: 'contain',
                              objectPosition: 'center',
                              transition: 'transform 0.3s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                          />
                          {isFreeCourse && (
                            <Badge className="position-absolute top-2 end-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-3 py-1 rounded-full shadow-md font-semibold">
                              Free
                            </Badge>
                          )}
                        </div>
                        <Card.Body className="d-flex flex-column p-4">
                          <div className="flex-grow-1">
                            <h5 className="fw-bold text-gray-900 mb-2 text-lg">{courseTitle}</h5>
                            <p className="text-gray-600 mb-3 text-sm" style={{ 
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              minHeight: '60px'
                            }}>
                              {course.description || 'No description available'}
                            </p>
                            <div className="mb-3 space-y-2">
                              {course.duration && (
                                <div className="d-flex align-items-center text-sm">
                                  <i className="fas fa-clock text-purple-600 me-2"></i>
                                  <span className="text-gray-700">{course.duration} mins</span>
                                </div>
                              )}
                              {course.category && (
                                <div className="d-flex align-items-center text-sm">
                                  <i className="fas fa-tag text-purple-600 me-2"></i>
                                  <span className="text-gray-700">{course.category}</span>
                                </div>
                              )}
                              {courseSkills.length > 0 && (
                                <div className="d-flex align-items-start text-sm">
                                  <i className="fas fa-lightbulb text-purple-600 me-2 mt-1"></i>
                                  <span className="text-gray-700">
                                    {courseSkills.slice(0, 2).join(', ')}{courseSkills.length > 2 ? '...' : ''}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="d-flex align-items-center mb-3 p-2 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg">
                              <i className="fas fa-rupee-sign text-purple-600 me-2"></i>
                              <span className="text-gray-900 font-semibold">{priceLabel}</span>
                            </div>
                          </div>
                          <div className="mt-auto">
                            {isFreeCourse ? (
                              <Button
                                bsPrefix="custom-hamburger"
                                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 "
                               
                                onClick={() => handleEnroll(course.id)}
                                disabled={loading.enrolling === course.id}
                                aria-label={`Enroll in ${courseTitle}`}
                              >
                                {loading.enrolling === course.id ? (
                                  <>
                                    <Spinner
                                      as="span"
                                      animation="border"
                                      size="sm"
                                      role="status"
                                      aria-hidden="true"
                                      className="me-2"
                                    />
                                    Enrolling...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-user-plus me-2"></i>
                                    Enroll Now
                                  </>
                                )}
                              </Button>
                            ) : (
                              <Button
                              bsPrefix="custom-hamburger"
                                className={`w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2`}                           
                                onClick={() => handleAddToCart(course.id)}
                                disabled={inCart || addingToCart === course.id}
                                aria-label={inCart ? `${courseTitle} already in cart` : `Add ${courseTitle} to cart`}
                              >
                                {addingToCart === course.id ? (
                                  <>
                                    <Spinner
                                      as="span"
                                      animation="border"
                                      size="sm"
                                      role="status"
                                      aria-hidden="true"
                                      className="me-2"
                                    />
                                    Adding...
                                  </>
                                ) : inCart ? (
                                  <>
                                    <i className="fas fa-check me-2"></i>
                                    In Cart
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-cart-plus me-2"></i>
                                    Add to Cart
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
      </div>
    </div>
  );
};

export default CoursesPage;